import { colors, fonts, spacing, borderRadius, shadows } from './index';

// File type constants
export const ACCEPTED_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
export const ACCEPTED_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

// File size limit (25MB in bytes)
export const MAX_FILE_SIZE = 25 * 1024 * 1024;

// Template data
export const TEMPLATE_HEADERS = [
  'Name',
  'Email',
  'Password',
  'Role',
  'Designation',
  'Region',
  'Department',
  'WhatsApp Number',
  'Is Active',
  'Projects',
];

export const TEMPLATE_SAMPLE_DATA = [
  [
    'John Doe',
    'john.doe@example.com',
    'Password123!',
    'GM',
    'General Manager',
    'NR',
    'Operations',
    '+919876543210',
    'Yes',
    'Project A',
  ],
  [
    'Jane Smith',
    'jane.smith@example.com',
    'Password123!',
    'GM',
    'General Manager',
    'SR',
    'Engineering',
    '+919876543211',
    'Yes',
    ['Project A', 'Project B'],
  ],
];

export const EXCEL_COLUMN_WIDTHS = [
  { wch: 20 }, // Name
  { wch: 30 }, // Email
  { wch: 15 }, // Password
  { wch: 10 }, // Role
  { wch: 20 }, // Designation
  { wch: 12 }, // Region
  { wch: 15 }, // Department
  { wch: 18 }, // WhatsApp Number
  { wch: 12 }, // Is Active
  { wch: 25 }, // Projects
];

export const TEMPLATE_FILENAMES = {
  CSV: 'users_import_template.csv',
  EXCEL: 'users_import_template.xlsx',
};

// Icon sizes
export const ICON_SIZES = {
  HEADER_ICON: '40px',
  HEADER_ICON_INNER: '22px',
  TITLE_FONT: '18px',
  CLOSE_ICON: '20px',
  UPLOAD_ICON: '48px',
  STATUS_ICON: '16px',
  FILE_ICON: '20px',
};

// Max heights for scrollable lists
export const MAX_HEIGHTS = {
  SUCCESS_LIST: '150px',
  ERROR_LIST: '200px',
};

// Color constants
export const IMPORT_COLORS = {
  YELLOW_GRADIENT_START: '#233491',
  YELLOW_GRADIENT_END: '#2f4ac4',
  YELLOW_ACTIVE: '#233491',
  YELLOW_BACKGROUND: 'rgba(35, 52, 145, 0.08)',
  YELLOW_SHADOW: 'rgba(35, 52, 145, 0.3)',
  DARK_TEXT: '#4C5BAD',
  BUTTON_PRIMARY: '#233491',
  BUTTON_PRIMARY_HOVER: '#2f4ac4',
  BUTTON_SHADOW: 'rgba(35, 52, 145, 0.25)',
  BUTTON_SHADOW_HOVER: 'rgba(35, 52, 145, 0.35)',
};

// Style constants
export const dialogTitleStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: fonts.fontSizeLg,
  fontWeight: fonts.weightBold,
  color: colors.textPrimaryDark,
  borderBottom: `1px solid ${colors.borderLight}`,
  pb: spacing.md,
  pt: spacing.md,
  px: spacing.md,
};

export const dialogContentStyles = {
  pt: spacing.md,
  pb: spacing.sm,
  px: spacing.md,
};

export const dialogActionsStyles = {
  borderTop: `1px solid ${colors.borderLight}`,
  px: spacing.md,
  py: spacing.sm,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: spacing.sm,
  backgroundColor: colors.backgroundSoft,
};

export const getUploadZoneStyles = (dragActive) => ({
  border: `2px dashed ${dragActive ? IMPORT_COLORS.YELLOW_ACTIVE : colors.borderDefault}`,
  borderRadius: borderRadius.input,
  backgroundColor: IMPORT_COLORS.YELLOW_BACKGROUND,
  p: spacing.lg,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    borderColor: IMPORT_COLORS.YELLOW_ACTIVE,
    backgroundColor: IMPORT_COLORS.YELLOW_BACKGROUND,
  },
});

export const fileInfoBoxStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  p: spacing.sm,
  backgroundColor: colors.backgroundSoft,
  borderRadius: borderRadius.input,
};

export const templateSectionStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.xs,
  p: spacing.md,
  backgroundColor: colors.backgroundSoft,
  borderRadius: borderRadius.input,
  border: `1px solid ${colors.borderLight}`,
};

export const importButtonStyles = {
  textTransform: 'none',
  borderRadius: borderRadius.button,
  padding: `${spacing.xs} ${spacing.md}`,
  fontSize: fonts.fontSizeSm,
  fontWeight: fonts.weightSemiBold,
  backgroundColor: IMPORT_COLORS.BUTTON_PRIMARY,
  background: `${IMPORT_COLORS.BUTTON_PRIMARY} !important`,
  backgroundImage: 'none !important',
  color: `${colors.textWhite} !important`,
  boxShadow: `0 4px 12px ${IMPORT_COLORS.BUTTON_SHADOW}`,
  border: 'none',
  '&:hover': {
    background: `${IMPORT_COLORS.BUTTON_PRIMARY_HOVER} !important`,
    backgroundImage: 'none !important',
    boxShadow: `0 6px 16px ${IMPORT_COLORS.BUTTON_SHADOW_HOVER}`,
    backgroundColor: IMPORT_COLORS.BUTTON_PRIMARY_HOVER,
  },
  '&.Mui-disabled': {
    background: `${colors.backgroundSoft} !important`,
    backgroundColor: `${colors.backgroundSoft} !important`,
    color: `${colors.textSecondary} !important`,
    boxShadow: 'none',
  },
};

export const successListStyles = {
  maxHeight: MAX_HEIGHTS.SUCCESS_LIST,
  overflowY: 'auto',
  border: `1px solid ${colors.borderDefault}`,
  borderRadius: borderRadius.input,
  p: spacing.xs,
  mt: spacing.xs,
  backgroundColor: colors.backgroundSoft,
};

export const errorListStyles = {
  maxHeight: MAX_HEIGHTS.ERROR_LIST,
  overflowY: 'auto',
  border: `1px solid ${colors.borderDefault}`,
  borderRadius: borderRadius.input,
  p: spacing.xs,
  mt: spacing.xs,
  backgroundColor: colors.backgroundSoft,
};

export const headerIconBoxStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: ICON_SIZES.HEADER_ICON,
  height: ICON_SIZES.HEADER_ICON,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${IMPORT_COLORS.YELLOW_GRADIENT_START} 0%, ${IMPORT_COLORS.YELLOW_GRADIENT_END} 100%)`,
  boxShadow: `0 2px 8px ${IMPORT_COLORS.YELLOW_SHADOW}`,
};

export const closeButtonStyles = {
  color: colors.textSecondary,
  '&:hover': {
    backgroundColor: colors.backgroundSoft,
    color: colors.textPrimaryDark,
  },
};

export const templateLinkStyles = {
  color: colors.primary,
  textDecoration: 'none',
  cursor: 'pointer',
  fontSize: fonts.fontSizeXs,
  fontWeight: fonts.weightSemiBold,
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  '&:hover': {
    textDecoration: 'underline',
  },
};

export const infoAlertStyles = {
  fontSize: fonts.fontSizeXs,
  backgroundColor: colors.primarySoft,
  border: `1px solid ${colors.primaryLighter}`,
  '& .MuiAlert-icon': {
    color: colors.primary,
  },
};
